// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
/**
 * Core metric configurations shared across Channels and Custom Channels features.
 *
 * These are the foundational metrics used by multiple features. Each feature can:
 * 1. Import and use these directly
 * 2. Extend with feature-specific metrics
 * 3. Override specific properties (e.g., render functions, drilldown columns)
 */

import React from "react";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import type { MetricCategory } from "@/components/metrics/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  type AccountData,
  formatAccountSourceRecordIdsForCSV,
  formatAccountsForCSV,
} from "@/lib/account-data";
import { formatDate } from "@/lib/date-formatting";
import { formatCurrency } from "@/lib/metrics/format-currency";
import { formatNumber } from "@/lib/metrics/format-number";
import { cn } from "@/lib/utils";
import type { UnifiedTableColumn } from "@/types/table-column";
import { formatRelationSource } from "@/utils/formatters";

// Use predefined pill colors from CATEGORY_STYLES
const ACCOUNT_PILL_CLASSES = CATEGORY_STYLES.Account.pillColor;
const OPPORTUNITY_PILL_CLASSES = CATEGORY_STYLES.Opportunity.pillColor;

// Common types for drilldown items
interface EngagementPeriodItem {
  first_engagement_date?: string;
  last_engagement_date?: string;
}

/**
 * Helper function to create consistent account link rendering
 * Used across multiple metric drilldown tables
 */
export const renderAccountLink = (item: unknown): React.ReactNode => {
  const typedItem = item as {
    name: string;
    account_name: string;
    account_id: string;
  };
  if (!typedItem.account_id) {
    return "";
  }

  const displayName = typedItem.account_name || typedItem.name;
  const explorerUrl = `/accounts/${typedItem.account_id}/explorer`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <a
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-pill={true}
          className={cn(
            "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
            ACCOUNT_PILL_CLASSES,
          )}
        >
          {displayName}
        </a>
      </TooltipTrigger>
      <TooltipContent>
        <p>View this account in Account Explorer</p>
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * Helper function to create campaign link rendering
 * Used in Total Engagements drilldown table
 */
export const renderCampaignLink = (item: unknown): React.ReactNode => {
  const typedItem = item as {
    campaign_name?: string;
    campaign_id?: string;
  };
  if (!typedItem.campaign_name) {
    return <span className="italic text-muted-foreground">-</span>;
  }

  // If campaign_id exists, create a link
  if (typedItem.campaign_id) {
    const campaignUrl = `/campaigns/${typedItem.campaign_id}/explorer`;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={campaignUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center hover:text-muted-foreground hover:bg-muted rounded px-2 py-1 -mx-2 -my-1 transition-colors"
          >
            {typedItem.campaign_name}
          </a>
        </TooltipTrigger>
        <TooltipContent>
          <p>View this campaign in Campaign Explorer</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Otherwise just show the name
  return <span>{typedItem.campaign_name}</span>;
};

/**
 * Helper function to create opportunity link rendering
 * Used across multiple metric drilldown tables
 */
export const renderOpportunityLink = (item: unknown): React.ReactNode => {
  const typedItem = item as {
    name: string;
    opportunity_name: string;
    opportunity_id: string;
    account_id: string;
  };
  const displayName = typedItem.opportunity_name || typedItem.name;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <a
          href={`/accounts/${typedItem.account_id}/explorer?opportunityId=${typedItem.opportunity_id}`}
          target="_blank"
          rel="noopener noreferrer"
          data-pill={true}
          className={cn(
            "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
            OPPORTUNITY_PILL_CLASSES,
          )}
        >
          {displayName}
        </a>
      </TooltipTrigger>
      <TooltipContent>
        <p>View this opportunity in Account Explorer</p>
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * Core metric configuration interface
 * Features can extend this with additional properties as needed
 */
export interface CoreMetricConfig {
  title: string;
  tooltip: string;
  category: MetricCategory;
  drilldownType: "table" | "chart";
  primaryKey: string[];
  drilldownColumns?: UnifiedTableColumn<unknown>[];
  defaultSort?: { key: string; direction: "asc" | "desc" };
  // Optional secondary metric relationship (e.g., accounts_engaged -> engagements_per_account)
  secondaryMetricKey?: string;
  // Optional function to generate the label for the secondary value
  getSecondaryValueLabel?: (secondaryValue: number | undefined) => string;
}

/**
 * Core metric configurations used by both Channels and Custom Channels
 *
 * These represent the fundamental engagement and opportunity metrics
 * that are common across different channel types.
 */
export const CORE_METRIC_CONFIGS: Record<string, CoreMetricConfig> = {
  accounts_engaged: {
    title: "Accounts Engaged",
    tooltip: "Number of unique accounts that had at least one engagement",
    category: "Account",
    drilldownType: "table",
    primaryKey: ["account_id"],
    defaultSort: { key: "total_engagements", direction: "desc" },
    secondaryMetricKey: "engagements_per_account",
    getSecondaryValueLabel: (value) =>
      ` ${value === 1 ? "engagement" : "engagements"}/account`,
    drilldownColumns: [
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
        defaultSortDirection: "desc",
        render: (item: unknown) => {
          const typedItem = item as { total_engagements?: number };
          return formatNumber(typedItem.total_engagements);
        },
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "-";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "-";
        },
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
  },

  total_engagements: {
    title: "Total Engagements",
    tooltip: "Total number of engagements across all accounts",
    category: "Touchpoint",
    drilldownType: "table",
    primaryKey: ["id"],
    defaultSort: { key: "engagement_date", direction: "desc" },
    drilldownColumns: [
      {
        key: "engagement_date",
        dataType: "date",
        title: "Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { engagement_date?: string };
          return typedItem.engagement_date
            ? formatDate(typedItem.engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { engagement_date?: string };
          return typedItem.engagement_date
            ? formatDate(typedItem.engagement_date)
            : "";
        },
      },
      {
        key: "person",
        title: "Person",
        sortable: true,
        filterable: false,
        sortKey: "first_name",
        render: (item: unknown) => {
          const typedItem = item as {
            first_name?: string;
            last_name?: string;
          };
          return (
            <span>
              {typedItem.first_name && typedItem.last_name
                ? `${typedItem.first_name} ${typedItem.last_name}`
                : "Unknown"}
            </span>
          );
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "email_address",
        title: "Email",
        sortable: true,
        filterable: false,
      },
      {
        key: "accounts",
        title: "Accounts",
        sortable: false,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          if (!typedItem.accounts || typedItem.accounts.length === 0) {
            return <span>-</span>;
          }
          return (
            <span>
              {typedItem.accounts.map((account, idx) => (
                <React.Fragment key={`${account.id}-${idx}`}>
                  {idx > 0 && ", "}
                  {renderAccountLink({
                    account_id: account.id,
                    name: account.name,
                  })}
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountsForCSV(typedItem.accounts);
        },
      },
      {
        key: "account_source_record_ids",
        title: "Account Source IDs",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountSourceRecordIdsForCSV(typedItem.accounts);
        },
      },
      {
        key: "campaign_name",
        title: "Campaign",
        sortable: true,
        filterable: false,
        render: renderCampaignLink,
      },
      {
        key: "campaign_source_record_id",
        title: "Campaign Source ID",
        exportOnly: true,
      },
    ],
  },

  people_engaged: {
    title: "People Engaged",
    tooltip: "Number of unique people that had at least one engagement",
    category: "Person",
    drilldownType: "table",
    primaryKey: ["person_id"],
    defaultSort: { key: "total_engagements", direction: "desc" },
    secondaryMetricKey: "engagements_per_person",
    getSecondaryValueLabel: (value) =>
      ` ${value === 1 ? "engagement" : "engagements"}/person`,
    drilldownColumns: [
      {
        key: "contact",
        title: "Contact",
        sortable: true,
        filterable: false,
        sortKey: "first_name",
        render: (item: unknown) => {
          const typedItem = item as {
            first_name?: string;
            last_name?: string;
            email_address?: string;
          };
          return (
            <div className="flex flex-col">
              <span className="font-medium">
                {typedItem.first_name && typedItem.last_name
                  ? `${typedItem.first_name} ${typedItem.last_name}`
                  : "Unknown"}
              </span>
              <span className="text-sm text-muted-foreground">
                {typedItem.email_address || "No email"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "job_title",
        title: "Job Title",
        render: (item: unknown) => {
          const typedItem = item as { job_title?: string };
          if (!typedItem.job_title)
            return (
              <span className="italic text-muted-foreground">No data</span>
            );
          return <span>{typedItem.job_title}</span>;
        },
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
        defaultSortDirection: "desc",
        render: (item: unknown) => {
          const typedItem = item as { total_engagements?: number };
          return formatNumber(typedItem.total_engagements);
        },
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                {`First: ${
                  typedItem.first_engagement_date
                    ? formatDate(typedItem.first_engagement_date)
                    : "N/A"
                }`}
              </span>
              <span className="text-sm text-muted-foreground">
                {`Last: ${
                  typedItem.last_engagement_date
                    ? formatDate(typedItem.last_engagement_date)
                    : "N/A"
                }`}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
  },

  opportunities_influenced: {
    title: "Opportunities Influenced",
    tooltip: "Number of opportunities with at least one engagement",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    defaultSort: { key: "created_date", direction: "desc" },
    secondaryMetricKey: "opportunities_influenced_amount",
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        sortKey: "name",
        render: renderOpportunityLink,
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        defaultSortDirection: "desc",
        render: (item: unknown) => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        defaultSortDirection: "desc",
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                {`First: ${
                  typedItem.first_engagement_date
                    ? formatDate(typedItem.first_engagement_date)
                    : "N/A"
                }`}
              </span>
              <span className="text-sm text-muted-foreground">
                {`Last: ${
                  typedItem.last_engagement_date
                    ? formatDate(typedItem.last_engagement_date)
                    : "N/A"
                }`}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "opportunity_source_record_id",
        title: "Opportunity Source ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
  },

  opportunities_influenced_amount: {
    title: "Sum of Amounts for All Opportunities Influenced",
    tooltip: "Total amount of opportunities influenced",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "opportunity_name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "amount",
        title: "Amount",
        sortable: true,
        filterable: false,
        defaultSortDirection: "desc",
        render: (item: unknown) => {
          const typedItem = item as { amount?: number };
          return formatCurrency(typedItem.amount);
        },
      },
    ],
  },

  opportunities_influenced_won: {
    title: "Won Opportunities Influenced",
    tooltip: "Number of won opportunities with at least one engagement",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    defaultSort: { key: "won_date", direction: "desc" },
    secondaryMetricKey: "opportunities_influenced_won_amount",
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        sortKey: "name",
        render: renderOpportunityLink,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            name?: string | null;
            opportunity_name?: string | null;
          };
          if (typedItem.opportunity_name) return typedItem.opportunity_name;
          if (typedItem.name) return typedItem.name;
          return "";
        },
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "won_date",
        dataType: "date",
        title: "Won Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { won_date?: string };
          return typedItem.won_date ? formatDate(typedItem.won_date) : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { won_date?: string };
          return typedItem.won_date ? formatDate(typedItem.won_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                {`First: ${
                  typedItem.first_engagement_date
                    ? formatDate(typedItem.first_engagement_date)
                    : "N/A"
                }`}
              </span>
              <span className="text-sm text-muted-foreground">
                {`Last: ${
                  typedItem.last_engagement_date
                    ? formatDate(typedItem.last_engagement_date)
                    : "N/A"
                }`}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
    ],
  },

  opportunities_influenced_won_amount: {
    title: "Sum of Amounts for Won Opportunities Influenced",
    tooltip: "Total amount of won opportunities influenced",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "opportunity_name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "amount",
        title: "Amount",
        sortable: true,
        filterable: false,
        defaultSortDirection: "desc",
        render: (item: unknown) => {
          const typedItem = item as { amount?: number };
          return formatCurrency(typedItem.amount);
        },
      },
    ],
  },

  engagements_per_account: {
    title: "Engagements per Account",
    tooltip: "Average number of engagements per account",
    category: "Engagement",
    drilldownType: "table",
    primaryKey: ["account_id"],
    drilldownColumns: [
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
        defaultSortDirection: "desc",
        render: (item: unknown) => {
          const typedItem = item as { total_engagements?: number };
          return formatNumber(typedItem.total_engagements);
        },
      },
      {
        key: "unique_people",
        title: "Unique People",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { unique_people?: number };
          return formatNumber(typedItem.unique_people);
        },
      },
    ],
  },

  engagements_per_person: {
    title: "Engagements per Person",
    tooltip:
      "Average number of engagements per person (total engagements divided by number of people engaged)",
    category: "Person",
    drilldownType: "table",
    primaryKey: ["person_id"],
    defaultSort: { key: "total_engagements", direction: "desc" },
    drilldownColumns: [
      {
        key: "contact",
        title: "Contact",
        sortable: true,
        filterable: false,
        sortKey: "first_name",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            first_name?: string;
            last_name?: string;
            email_address?: string;
          };
          return (
            <div className="flex flex-col">
              <span className="font-medium">
                {typedItem.first_name && typedItem.last_name
                  ? `${typedItem.first_name} ${typedItem.last_name}`
                  : "Unknown"}
              </span>
              <span className="text-sm text-muted-foreground">
                {typedItem.email_address || "No email"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
    ],
  },

  deal_contacts_engaged: {
    title: "Deal Contacts Engaged",
    tooltip:
      "Count of people who engaged and have been in the buying group for any opportunity. Includes buying group associations via both CRM contact roles and Upside's healed buying groups. Excludes any people from your own team.",
    category: "Person",
    drilldownType: "table",
    primaryKey: ["person_id", "last_name", "first_name"],
    defaultSort: { key: "total_engagements", direction: "desc" },
    secondaryMetricKey: "engagements_per_deal_contact",
    getSecondaryValueLabel: (value) =>
      ` ${value === 1 ? "engagement" : "engagements"}/contact`,
    drilldownColumns: [
      {
        key: "contact",
        title: "Contact",
        sortable: true,
        filterable: false,
        sortKey: "last_name",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            first_name?: string;
            last_name?: string;
            email_address?: string;
          };
          return (
            <div className="flex flex-col">
              <span className="font-medium">
                {typedItem.first_name && typedItem.last_name
                  ? `${typedItem.first_name} ${typedItem.last_name}`
                  : "Unknown"}
              </span>
              <span className="text-sm text-muted-foreground">
                {typedItem.email_address || "No email"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "accounts",
        title: "Accounts",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "job_title",
        title: "Job Title",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { job_title?: string };
          if (!typedItem.job_title)
            return (
              <span className="italic text-muted-foreground">No data</span>
            );
          return <span>{typedItem.job_title}</span>;
        },
      },
      {
        key: "relation_source",
        title: "Relation Source",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { relation_source?: string };
          return (
            <span>
              {typedItem.relation_source
                ? formatRelationSource(typedItem.relation_source)
                : ""}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { relation_source?: string };
          return typedItem.relation_source
            ? formatRelationSource(typedItem.relation_source)
            : "";
        },
      },
      {
        key: "opportunities",
        title: "Opportunities",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            opportunities?: Array<{
              id: string;
              name: string;
              source_record_id: string;
              account_id: string;
            }>;
          };

          const opportunities = typedItem.opportunities;
          if (!opportunities || opportunities.length === 0)
            return <span>-</span>;

          return (
            <div className="flex flex-wrap gap-1">
              {opportunities.map((opportunity, idx) => {
                const { id: oppId, name, account_id: accountId } = opportunity;

                if (oppId && accountId) {
                  return (
                    <Tooltip key={`${oppId}-${idx}`}>
                      <TooltipTrigger asChild>
                        <a
                          href={`/accounts/${accountId}/explorer?opportunityId=${oppId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-pill={true}
                          className={cn(
                            "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
                            OPPORTUNITY_PILL_CLASSES,
                          )}
                        >
                          {name}
                        </a>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>View this opportunity in Account Explorer</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                return (
                  <span
                    key={`${oppId || name}-${idx}`}
                    className={cn(
                      "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
                      OPPORTUNITY_PILL_CLASSES.replace(
                        "cursor-pointer",
                        "",
                      ).replace(
                        "hover:bg-supporting-green/20 dark:hover:bg-supporting-green/20 transition-colors",
                        "",
                      ),
                    )}
                  >
                    {name}
                  </span>
                );
              })}
            </div>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            opportunities?: Array<{ name: string }>;
          };
          return (
            typedItem.opportunities?.map((opp) => opp.name).join(", ") || ""
          );
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { total_engagements?: number };
          return formatNumber(typedItem.total_engagements);
        },
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                {`First: ${
                  typedItem.first_engagement_date
                    ? formatDate(typedItem.first_engagement_date)
                    : "N/A"
                }`}
              </span>
              <span className="text-sm text-muted-foreground">
                {`Last: ${
                  typedItem.last_engagement_date
                    ? formatDate(typedItem.last_engagement_date)
                    : "N/A"
                }`}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
    ],
  },

  engagements_per_deal_contact: {
    title: "Engagements per Deal Contact",
    tooltip:
      "Average number of engagements per buying group person. Includes buying group associations via both CRM contact roles and Upside's healed buying groups. Excludes any people from your own team.",
    category: "Person",
    drilldownType: "table",
    primaryKey: ["person_id", "account_id"],
    defaultSort: { key: "engagements_per_opportunity", direction: "desc" },
    drilldownColumns: [
      {
        key: "contact",
        title: "Contact",
        sortable: true,
        filterable: false,
        sortKey: "last_name",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            first_name?: string;
            last_name?: string;
            email_address?: string;
          };
          return (
            <div className="flex flex-col">
              <span className="font-medium">
                {typedItem.first_name && typedItem.last_name
                  ? `${typedItem.first_name} ${typedItem.last_name}`
                  : "Unknown"}
              </span>
              <span className="text-sm text-muted-foreground">
                {typedItem.email_address || "No email"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagements_per_opportunity",
        title: "Engagements per Opportunity",
        sortable: true,
        filterable: false,
      },
    ],
  },

  opportunities_influenced_in_cycle: {
    title: "Opportunities Influenced In-Cycle",
    tooltip:
      "Count and total amount of opportunities influenced during their active cycle",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    defaultSort: { key: "created_date", direction: "desc" },
    secondaryMetricKey: "opportunities_influenced_in_cycle_amount",
    drilldownColumns: [
      {
        key: "opportunity_name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        defaultSortDirection: "desc",
        render: (item: unknown) => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        defaultSortDirection: "desc",
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                {`First: ${
                  typedItem.first_engagement_date
                    ? formatDate(typedItem.first_engagement_date)
                    : "N/A"
                }`}
              </span>
              <span className="text-sm text-muted-foreground">
                {`Last: ${
                  typedItem.last_engagement_date
                    ? formatDate(typedItem.last_engagement_date)
                    : "N/A"
                }`}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "opportunity_source_record_id",
        title: "Opportunity Source ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
  },

  opportunities_influenced_in_cycle_amount: {
    title: "Sum of Amounts for Opportunities Influenced In-Cycle",
    tooltip:
      "Sum of the total amount of opportunities influenced during their active cycle",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "opportunity_name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_amount",
        title: "Amount",
        sortable: true,
        filterable: false,
        defaultSortDirection: "desc",
        render: (item: unknown) => {
          const typedItem = item as { total_amount?: number };
          return formatCurrency(typedItem.total_amount);
        },
      },
    ],
  },

  opportunities_created_post_engagement: {
    title: "Opportunities Created Post-Engagement",
    tooltip:
      "Count and total amount of opportunities that were created after engagement",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    secondaryMetricKey: "opportunities_created_post_engagement_amount",
    drilldownColumns: [
      {
        key: "opportunity_name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
      },
      {
        key: "days_till_creation",
        title: "Days from Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { days_till_creation: number };
          return typedItem.days_till_creation !== null
            ? `${typedItem.days_till_creation} days`
            : "-";
        },
      },
    ],
  },

  opportunities_created_post_engagement_amount: {
    title: "Sum of Amounts for Opportunities Created Post-Engagement",
    tooltip:
      "Sum of the total amount of opportunities that were created after engagement",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    defaultSort: { key: "total_amount", direction: "desc" },
    drilldownColumns: [
      {
        key: "opportunity_name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
      },
      {
        key: "days_till_creation",
        title: "Days from Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const value = item as { days_till_creation: number };
          return value.days_till_creation !== null
            ? `${value.days_till_creation} days`
            : "-";
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                {`First: ${
                  typedItem.first_engagement_date
                    ? formatDate(typedItem.first_engagement_date)
                    : "N/A"
                }`}
              </span>
              <span className="text-sm text-muted-foreground">
                {`Last: ${
                  typedItem.last_engagement_date
                    ? formatDate(typedItem.last_engagement_date)
                    : "N/A"
                }`}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
    ],
  },

  progressed_opportunities: {
    title: "Progressed Opportunities",
    tooltip:
      "Count and total amount of opportunities that progressed after engagement",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["name", "account_name"],
    defaultSort: { key: "progressed_date", direction: "desc" },
    secondaryMetricKey: "progressed_opportunities_amount",
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "stage_progression",
        title: "Stage Progression",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            stage_progression?: string;
            stage_at_engagement?: string;
          };
          if (!typedItem.stage_progression) return <span>N/A</span>;

          const stages = typedItem.stage_progression.split(" → ");
          const engagementStageIndex = stages.indexOf(
            typedItem.stage_at_engagement ?? "",
          );

          return (
            <span>
              {stages.map((stage, index) => (
                <React.Fragment key={index}>
                  {index > 0 && " → "}
                  <span
                    className={index > engagementStageIndex ? "font-bold" : ""}
                  >
                    {stage}
                  </span>
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { stage_progression?: string };
          if (!typedItem.stage_progression) return "N/A";
          return typedItem.stage_progression;
        },
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
      },
      {
        key: "progressed_date",
        dataType: "date",
        title: "Progressed Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { progressed_date: string };
          return value.progressed_date
            ? formatDate(value.progressed_date)
            : "N/A";
        },
        exportValue: (item: unknown): string => {
          const value = item as { progressed_date: string };
          return value.progressed_date ? formatDate(value.progressed_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "first_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                {`First: ${
                  typedItem.first_engagement_date
                    ? formatDate(typedItem.first_engagement_date)
                    : "N/A"
                }`}
              </span>
              <span className="text-sm text-muted-foreground">
                {`Last: ${
                  typedItem.last_engagement_date
                    ? formatDate(typedItem.last_engagement_date)
                    : "N/A"
                }`}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
    ],
  },

  progressed_opportunities_amount: {
    title: "Sum of Amounts for Progressed Opportunities",
    tooltip:
      "Sum of the total amount of opportunities that progressed after engagement",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Amount",
        sortable: true,
        filterable: false,
        defaultSortDirection: "desc",
        render: (item: unknown) => {
          const typedItem = item as { total_amount?: number };
          return formatCurrency(typedItem.total_amount);
        },
      },
    ],
  },

  opportunities_activated: {
    title: "Opportunities with Activation Milestone Citation",
    tooltip:
      "Count of opportunities where AI analysis identified touchpoints as influential in opportunity activation",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    defaultSort: { key: "total_amount", direction: "desc" },
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            name?: string | null;
            opportunity_name?: string | null;
          };
          if (typedItem.opportunity_name) return typedItem.opportunity_name;
          if (typedItem.name) return typedItem.name;
          return "";
        },
      },
      {
        key: "accounts",
        title: "Accounts",
        sortable: false,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          if (!typedItem.accounts || typedItem.accounts.length === 0) {
            return <span>-</span>;
          }
          return (
            <span>
              {typedItem.accounts.map((account, idx) => (
                <React.Fragment key={`${account.id}-${idx}`}>
                  {idx > 0 && ", "}
                  {renderAccountLink({
                    account_id: account.id,
                    name: account.name,
                  })}
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: AccountData[];
          };
          return formatAccountsForCSV(typedItem.accounts);
        },
      },
      {
        key: "account_source_record_ids",
        title: "Account Source IDs",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountSourceRecordIdsForCSV(typedItem.accounts);
        },
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
      },
      {
        key: "is_won",
        title: "Status",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { is_won?: boolean };
          return typedItem.is_won ? (
            <span className="text-success font-medium">Won</span>
          ) : (
            <span className="text-muted-foreground">Open</span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { is_won?: boolean };
          return typedItem.is_won ? "Won" : "Open";
        },
      },
      {
        key: "analysis_type",
        title: "Analysis Type",
        sortable: true,
        filterable: false,
      },
      {
        key: "influence_source",
        title: "Influence Source",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { influence_source?: string };
          const source = typedItem.influence_source || "";
          const labelMap: Record<string, string> = {
            touchpoint_citations: "Touchpoint Citations",
            campaign_influences: "Campaign Influences",
            channel_influences: "Channel Influences",
          };
          return <span>{labelMap[source] || source}</span>;
        },
      },
      {
        key: "total_engagements",
        title: "Channel Touchpoints",
        sortable: true,
        filterable: false,
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
    ],
  },

  opportunities_activated_amount: {
    title: "Sum of Amounts for All Opportunities Activated",
    tooltip:
      "Sum of the total amount of all opportunities where AI analysis identified touchpoints as influential",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "opportunity_name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "amount",
        title: "Amount",
        sortable: true,
        filterable: false,
        defaultSortDirection: "desc",
        render: (item: unknown) => {
          const typedItem = item as { amount?: number };
          return formatCurrency(typedItem.amount);
        },
      },
    ],
  },

  opportunities_activated_won: {
    title: "Won Opportunities with Activation Milestone Citation",
    tooltip:
      "Count of won opportunities where AI analysis identified touchpoints as influential",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    defaultSort: { key: "total_amount", direction: "desc" },
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            name?: string | null;
            opportunity_name?: string | null;
          };
          if (typedItem.opportunity_name) return typedItem.opportunity_name;
          if (typedItem.name) return typedItem.name;
          return "";
        },
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { total_amount?: number | null };
          return formatCurrency(typedItem.total_amount);
        },
      },
      {
        key: "is_won",
        title: "Status",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { is_won?: boolean };
          return typedItem.is_won ? (
            <span className="text-success font-medium">Won</span>
          ) : (
            <span className="text-muted-foreground">Open</span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { is_won?: boolean };
          return typedItem.is_won ? "Won" : "Open";
        },
      },
      {
        key: "analysis_type",
        title: "Analysis Type",
        sortable: true,
        filterable: false,
      },
      {
        key: "influence_source",
        title: "Influence Source",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { influence_source?: string };
          const source = typedItem.influence_source || "";
          const labelMap: Record<string, string> = {
            touchpoint_citations: "Touchpoint Citations",
            campaign_influences: "Campaign Influences",
            channel_influences: "Channel Influences",
          };
          return <span>{labelMap[source] || source}</span>;
        },
      },
      {
        key: "total_engagements",
        title: "Channel Touchpoints",
        sortable: true,
        filterable: false,
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
    ],
  },

  opportunities_activated_won_amount: {
    title:
      "Sum of Amounts for All Won Opportunities with Activation Milestone Citation",
    tooltip:
      "Sum of the total amount of all won opportunities where AI analysis identified touchpoints as influential",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "opportunity_name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "amount",
        title: "Amount",
        sortable: true,
        filterable: false,
        defaultSortDirection: "desc",
        render: (item: unknown) => {
          const typedItem = item as { amount?: number };
          return formatCurrency(typedItem.amount);
        },
      },
    ],
  },

  accounts_activated: {
    title: "Accounts Activated",
    tooltip:
      "Count of accounts that had no engagement at any time prior to this engagement",
    category: "Account",
    drilldownType: "table",
    primaryKey: ["account_id"],
    defaultSort: { key: "first_engagement_date", direction: "desc" },
    drilldownColumns: [
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { total_engagements?: number };
          return formatNumber(typedItem.total_engagements);
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { unique_people?: number };
          return formatNumber(typedItem.unique_people);
        },
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "-";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "-";
        },
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
  },

  accounts_reactivated: {
    title: "Accounts Reactivated",
    tooltip:
      "Count of accounts that had no engagement for 90 days prior to this engagement",
    category: "Account",
    drilldownType: "table",
    primaryKey: ["account_id"],
    defaultSort: { key: "days_inactive", direction: "desc" },
    drilldownColumns: [
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { total_engagements?: number };
          return formatNumber(typedItem.total_engagements);
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { unique_people?: number };
          return formatNumber(typedItem.unique_people);
        },
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "-";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "-";
        },
      },
      {
        key: "days_inactive",
        title: "Days Inactive",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { days_inactive?: number };
          return formatNumber(typedItem.days_inactive);
        },
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
  },

  accounts_without_opps: {
    title: "Accounts Engaged Outside of Opportunity Cycle",
    tooltip:
      "Count of accounts that have engaged and have no associated opportunities in the last 90 days (based on opportunity created or close date). This can help identify potential pipeline gaps.",
    category: "Account",
    drilldownType: "table",
    primaryKey: ["account_id"],
    defaultSort: { key: "total_engagements", direction: "desc" },
    drilldownColumns: [
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { total_engagements?: number };
          return formatNumber(typedItem.total_engagements);
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { unique_people?: number };
          return formatNumber(typedItem.unique_people);
        },
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "-";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "-";
        },
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
  },
};

/**
 * Core metric data types for CSV export and formatting
 */
export const CORE_METRIC_DATA_TYPES: Record<string, string> = {
  accounts_engaged: "integer",
  total_engagements: "integer",
  people_engaged: "integer",
  opportunities_influenced: "integer",
  opportunities_influenced_amount: "currency",
  opportunities_influenced_won: "integer",
  opportunities_influenced_won_amount: "currency",
  engagements_per_account: "float",
  engagements_per_person: "float",
  deal_contacts_engaged: "integer",
  engagements_per_deal_contact: "float",
  opportunities_influenced_in_cycle: "integer",
  opportunities_influenced_in_cycle_amount: "currency",
  opportunities_created_post_engagement: "integer",
  opportunities_created_post_engagement_amount: "currency",
  progressed_opportunities: "integer",
  progressed_opportunities_amount: "currency",
  opportunities_activated: "integer",
  opportunities_activated_amount: "currency",
  opportunities_activated_won: "integer",
  opportunities_activated_won_amount: "currency",
  accounts_activated: "integer",
  accounts_reactivated: "integer",
  accounts_without_opps: "integer",
};
